import React, { useEffect } from 'react';
import { redirect } from '../../../helper/redirectHelper';
import { Container, Entry, SubTitle, Title } from './CPCOntologyReferencePage.styled';
import Terms from '../../components/common/Terms';

const CPCOntologyReferencePage = () => {
    useEffect(() => {
        const handleAnchorClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            const targetId = (event.currentTarget as HTMLAnchorElement).getAttribute('href')?.substring(1);
            if (targetId) {
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                    event.preventDefault();
                    targetElement.scrollIntoView({ behavior: 'smooth' });
                }
            }
        };

        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', handleAnchorClick as unknown as EventListener);
        });

        return () => {
            document.querySelectorAll('a[href^="#"]').forEach(anchor => {
                anchor.removeEventListener('click', handleAnchorClick as unknown as EventListener);
            });
        };
    }, []);
    return (
        <Container className="container">
            <Title data-testid="title">CPC Ontology Reference</Title>
            <div>
                <SubTitle data-testid="classes">Classes</SubTitle>
                <Entry id="Class">
                    <div className="heading">
                        <a href={redirect('def/cpc/Class')}>Class</a>
                    </div>
                    <div>A Class node in the cooperative patent classification (cpc) scheme.</div>
                    <div>
                        super classes <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <div id="Classification">
                    <div className="heading">
                        <a href={redirect('def/cpc/Classification')}>Classification</a>
                    </div>
                    <div>A node in the cooperative classification scheme.</div>
                    <div>
                        sub classes <a href="#Class">Class</a> <a href="#SubSection">SubSection</a>{' '}
                        <a href="#SubGroup">SubGroup</a> <a href="#MainGroup">MainGroup</a>{' '}
                        <a href="#SubClass">SubClass</a> <a href="#Section">Section</a>
                    </div>
                    <div>
                        super classes <a href="#Node">Node</a>
                    </div>
                </div>
                <Entry id="GuidanceHeading">
                    <div className="heading">
                        <a href={redirect('def/cpc/GuidanceHeading')}>GuidanceHeading</a>
                    </div>
                    <div>
                        <div>The class of CPC guidance headings.</div>
                        <div>
                            super classes <a href="#Node">Node</a>
                        </div>
                    </div>
                </Entry>
                <Entry id="MainGroup">
                    <div className="heading">
                        <a href={redirect('def/cpc/MainGroup')}>MainGroup</a>
                    </div>
                    <div>The class of CPC guidance headings.</div>
                    <div>
                        super classes <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="Node">
                    <div className="heading">
                        <a href={redirect('def/cpc/Node')}>Node</a>
                    </div>
                    <div>The class of node in the cooperative patent classification scheme.</div>
                    <div>
                        sub classes <a href="#GuidanceHeading">GuidanceHeading</a>{' '}
                        <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="Section">
                    <div className="heading">
                        <a href={redirect('def/cpc/Section')}>Section</a>
                    </div>
                    <div>A top level concept in the cooperative patent classification (cpc) scheme.</div>
                    <div>
                        super classes <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="SubClass">
                    <div className="heading">
                        <a href={redirect('def/cpc/SubClass')}>SubClass</a>
                    </div>
                    <div>A subclass node in the cooperative patent classification (cpc) scheme.</div>
                    <div>
                        super classes <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="SubGroup">
                    <div className="heading">
                        <a href={redirect('def/cpc/SubGroup')}>SubGroup</a>
                    </div>
                    <div>A subgroup node in the Cooperative Patent Classification (cpc) scheme.</div>
                    <div>
                        super classes <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="SubSection">
                    <div className="heading">
                        <a href={redirect('def/cpc/SubSection')}>SubSection</a>
                    </div>
                    <div>A sub-section node in the cooperative patent classification scheme.</div>
                    <div>
                        super classes <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <SubTitle data-testid="properties">Properties</SubTitle>
                <Entry id="classification">
                    <div className="heading">
                        <a href={redirect('def/cpc/classification')}>classification</a>
                    </div>
                    <div>
                        The property :classification is a general purpose property that relates a resource to a CPC
                        classification.
                    </div>
                    <div>
                        rdfs:range <a href="#classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="concordantCPC">
                    <div className="heading">
                        <a href={redirect('def/cpc/concordantCPC')}>concordantCPC</a>
                    </div>
                    <div>
                        The property :cpcConcordant relates a node in the international patent classification (ipc)
                        scheme to a close match in the cooperative patent classification (cpc) scheme.
                    </div>
                    <div>
                        rdfs:range <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="concordantIPC">
                    <div className="heading">
                        <a href={redirect('def/cpc/concordantIPC')}>concordantIPC</a>
                    </div>
                    <div>
                        The property :ipcConcordant relates a node in the cooperative classification (cpc) scheme to a
                        close match in the international patent classification (ipc) scheme.
                    </div>
                    <div>
                        rdfs:domain <a href="#Classification">Classification</a>
                    </div>
                    <div>
                        rdfs:range <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="fullTitle">
                    <div className="heading">
                        <a href={redirect('def/cpc/fullTitle')}>fullTitle</a>
                    </div>
                    <div>The property fullTitle relates a CPC node to its full title including references.</div>
                    <div>
                        rdfs:domain <a href="#Node">Node</a>
                    </div>
                </Entry>
                <Entry id="guidanceHeading">
                    <div className="heading">
                        <a href={redirect('def/cpc/guidanceHeading')}>guidanceHeading</a>
                    </div>
                    <div>The property :guidanceHeading relates a node in the CPC hierarchy to a Guidance Heading.</div>
                    <div>
                        rdfs:domain <a href="#Classification" data-testid="Classification">Classification</a>
                    </div>
                    <div>
                        rdfs:range <a href="#GuidanceHeading">GuidanceHeading</a>
                    </div>
                </Entry>
                <Entry id="level">
                    <div className="heading">
                        <a href={redirect('def/cpc/level')}>level</a>
                    </div>
                    <div>The property :level states the level of a node in the cooperative classification scheme.</div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#int">int</a>
                    </div>
                </Entry>
                <Entry id="sortKey">
                    <div className="heading">
                        <a href={redirect('def/cpc/sortKey')}>sortKey</a>
                    </div>
                    <div>
                        The property :sortKey has a string value that may be used to sort the nodes within a level of
                        the cooperative patent classification scheme.
                    </div>
                    <div>
                        rdfs:domain <a href="#Classification">Classification</a>
                    </div>
                </Entry>
                <Entry id="symbol">
                    <div className="heading">
                        <a href={redirect('def/cpc/symbol')}>symbol</a>
                    </div>
                    <div>
                        The property :symbol is a general purpose property that relates a resource to the symbol for a
                        node in the cooperative patent classification (cpc) scheme.
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#string">string</a>
                    </div>
                </Entry>
                <Entry id="titleXML">
                    <div className="heading">
                        <a href={redirect('def/cpc/titleXML')}>titleXML</a>
                    </div>
                    <div>
                        The property :titleXML relates a node in the cooperative patent classification scheme with an
                        xml representation of its title. The range of this property is a string so that XML does not
                        have to be in a particular normal form.
                    </div>
                    <div>
                        rdfs:range <a href="https://www.w3.org/2001/XMLSchema#string">string</a>
                    </div>
                </Entry>
                <SubTitle data-testid="individuals">Individuals</SubTitle>
                <Entry id="CPCNotation">
                    <div className="heading">
                        <a href={redirect('def/cpc/CPCNotation')}>CPCNotation</a>
                    </div>
                    <div>The datatype of CPC notations.</div>
                    <div>
                        type <a href="https://www.w3.org/2000/01/rdf-schema#Datatype">Datatype</a>
                    </div>
                </Entry>
                <Entry id="cpc">
                    <div className="heading">
                        <a href={redirect('def/cpc/')}>cpc</a>
                    </div>
                    <div>An ontology for representing the Cooperative Patent Classification (CPC) scheme.</div>
                    <div>
                        type <a href="https://www.w3.org/2002/07/owl#Ontology">Ontology</a>
                    </div>
                </Entry>
                <Entry id="scheme">
                    <div className="heading">
                        <a href={redirect('def/cpc/scheme')}>scheme</a>
                    </div>
                    <div>The cooperative patent classification scheme.</div>
                    <div>
                        type <a href="https://www.w3.org/2004/02/skos/core#ConceptScheme">ConceptScheme</a>
                    </div>
                </Entry>
            </div>
            <Terms />
        </Container>
    );
};
export default CPCOntologyReferencePage;